import React, { useEffect, useMemo, useRef } from 'react';
import { buildSymbols, drawPane, nearestSymbol, symbolRadius } from './pseudosection-helpers';

/**
 * One pseudosection pane drawn on a canvas (up to ~50k symbols). Hover reports the nearest
 * reading, click picks it; `highlight` / `hover` are point indices rings are drawn around, so
 * the same quadrupole lights up in every pane.
 */
export default function PseudosectionPane({
  pane, points, layout, rows, electrodes, hollowTiers, highlight, hover, onHover, onPick, registerCanvas, bg, fg,
}) {
  const ref = useRef(null);
  const symbols = useMemo(
    () => buildSymbols({ points, values: pane.values, scale: pane.scale, layout, rows, hollowTiers }),
    [points, pane, layout, rows, hollowTiers],
  );
  const radius = useMemo(() => symbolRadius(points.x, layout), [points, layout]);
  const dpr = typeof window !== 'undefined' ? Math.min(2, window.devicePixelRatio || 1) : 1;

  useEffect(() => {
    if (registerCanvas) registerCanvas(pane.id, ref.current);
    return () => { if (registerCanvas) registerCanvas(pane.id, null); };
  }, [registerCanvas, pane.id]);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas && canvas.getContext ? canvas.getContext('2d') : null;
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawPane(ctx, { layout, pane, symbols, electrodes, radius, highlight, hover, rows, bg, fg });
  }, [layout, pane, symbols, electrodes, radius, highlight, hover, rows, dpr, bg, fg]);

  const locate = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const k = layout.W / (r.width || layout.W);
    return nearestSymbol(symbols, (e.clientX - r.left) * k, (e.clientY - r.top) * k, Math.max(8, radius + 4));
  };

  return (
    <canvas
      ref={ref}
      data-testid={`pseudosection-pane-${pane.id}`}
      width={Math.round(layout.W * dpr)}
      height={Math.round(layout.H * dpr)}
      style={{ width: '100%', aspectRatio: `${layout.W} / ${layout.H}`, display: 'block', cursor: 'crosshair' }}
      onMouseMove={(e) => { const s = locate(e); onHover(s ? s.i : -1); }}
      onMouseLeave={() => onHover(-1)}
      onClick={(e) => { const s = locate(e); onPick(s ? s.i : -1); }}
    />
  );
}
